import {
    Admin,
    CanAccess,
    CustomRoutes,
    RaThemeOptions,
    Resource,
    defaultTheme,
    localStorageStore,
} from 'react-admin';

import { deepmerge } from '@mui/utils';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from 'ra-language-english';
import { Route, Navigate } from 'react-router';
import { authProvider, dataProvider } from '@/providers/loginWithEmail';

// Modules
import Layout from '@/components/layout/Layout';
import quotes from '@/modules/quotes';
import projects from '@/modules/projects';
import contractor_owners from '@/modules/contractor_owners';
import users from '@/modules/users';
import roles from '@/modules/roles';
import project_file_configs from '@/modules/project_file_configs';
import tickets from '@/modules/tickets';
import leads from '@/modules/leads';
import help_documents from '@/modules/help_documents';
import municipalities from '@/modules/municipalities';
import permit_files from '@/modules/permit-files';
import cards from '@/modules/cards';
import { LoginPage } from '@/modules/login/LoginPage';
import { SettingsLayout } from '@/modules/settings/SettingsLayout';
import { DashboardPage } from '@/modules/dashboard/DashboardPage';
import { QuoteFolderShow } from '@/modules/quotes/QuoteFolderShow';
import { ReceiptsPage } from '@/modules/receipts/ReceiptsPage';
import { ReceiptAdminPage } from '@/modules/receipts/ReceiptAdminPage';
import { QboClassesPage } from '@/modules/receipts/QboClassMapPanel';

import {
    ConfigurationContextValue,
    ConfigurationProvider,
} from './ConfigurationContext';
import {
    defaultLogo,
    defaultTitle,
} from './defaultConfiguration';

// Define the interface for the CRM component props
type CRMProps = {
    lightTheme?: RaThemeOptions;
    darkTheme?: RaThemeOptions;
} & Partial<ConfigurationContextValue>;

const defaultLightTheme = deepmerge(defaultTheme, {
    palette: {
        background: {
            default: '#F7F9FC',
        },
        primary: {
            main: '#2F68AC',
        },
    },
    components: {
        RaCreateButton: {
            styleOverrides: {
                root: {
                    // iOS Safari and other touch devices: meet ~48px tap target (Apple HIG 44pt).
                    '@media (hover: none) and (pointer: coarse)': {
                        '&:not(.RaCreateButton-floating)': {
                            minHeight: 48,
                            fontSize: '1rem',
                            padding: '10px 20px',
                        },
                    },
                },
                floating: {
                    '@media (hover: none) and (pointer: coarse)': {
                        width: 64,
                        height: 64,
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.75rem',
                        },
                    },
                },
            },
        },
        RaFileInput: {
            styleOverrides: {
                root: {
                    '& .RaFileInput-dropZone': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                },
            },
        },
    },
});

const i18nProvider = polyglotI18nProvider(
    () => englishMessages,
    'en',
    [{ locale: 'en', name: 'English' }],
    { allowMissing: true }
);

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `react-admin`. It provides
 * default configurations and themes but allows for customization through props. The component
 * wraps the application with a `ConfigurationProvider` to provide configuration values via context.
 *
 * @param {Array<ContactGender>} contactGender - The gender options for quotes used in the application.
 * @param {string[]} companySectors - The list of company sectors used in the application.
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {string[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the CRM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {string[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from './CRM';
 *
 * const App = () => (
 *     <CRM
 *         logo="/path/to/logo.png"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
    darkTheme,
    lightTheme = defaultLightTheme,
    logo = defaultLogo,
    title = defaultTitle,
}: CRMProps) => (
    <ConfigurationProvider
        logo={logo}
        title={title}
    >
        <Admin
            dataProvider={dataProvider}
            authProvider={authProvider}
            store={localStorageStore(undefined, 'CRM')}
            layout={Layout}
            loginPage={LoginPage}
            dashboard={DashboardPage}
            title={title}
            theme={lightTheme}
            darkTheme={darkTheme || null}
            i18nProvider={i18nProvider}
            requireAuth
        >
            <CustomRoutes>
                <Route path={SettingsLayout.path} element={<SettingsLayout />} />
                <Route path="/site-config" element={<Navigate to="/settings/site-config" replace />} />
                <Route path="/estimator" element={<Navigate to="/quotes/create" replace />} />
                <Route path="/estimator/*" element={<Navigate to="/quotes/create" replace />} />
                <Route path="/quotes/folders/:projectId" element={<QuoteFolderShow />} />
                <Route
                    path="/receipts"
                    element={
                        <CanAccess resource="receipt" action="read">
                            <ReceiptsPage />
                        </CanAccess>
                    }
                />
                <Route path="/receipts/admin/*" element={<ReceiptAdminPage />} />
                <Route
                    path="/receipts/classes"
                    element={
                        <CanAccess resource="receipt" action="admin_reconciliation">
                            <QboClassesPage />
                        </CanAccess>
                    }
                />
            </CustomRoutes>
            <Resource name="quotes" {...quotes} />
            <Resource name="projects" {...projects} />
            <Resource name="contractor_owners" {...contractor_owners} />
            <Resource name="related-people" />
            <Resource name="users" {...users} />
            <Resource name="roles" {...roles} />
            <Resource name="project_file_configs" {...project_file_configs} />
            <Resource name="tickets" {...tickets} />
            <Resource name="cards" {...cards} />
            <Resource name="leads" {...leads} />
            <Resource name="help_documents" {...help_documents} />
            <Resource name="receipts" />
            <Resource name="expense_transactions" />
            <Resource name="municipalities" {...municipalities} />
            <Resource name="permit-files" {...permit_files} />
        </Admin>
    </ConfigurationProvider>
);
